import Image from 'next/image';
import { useRouter } from 'next/router';
import { FC } from 'react';

import { DetailsPayment } from '@/components/modules/details-payment';
import { MessageSmileModal } from '@/components/modules/message-smile-modal';
import { ButtonDetail } from '@/components/modules/orders-page/orders/ui/body/button-detail';
import { GeneralInfo } from '@/components/modules/orders-page/orders/ui/general-info';
import { TProductItemConfig } from '@/components/modules/orders-page/orders/ui/product-item';
import { RentCalendar } from '@/components/modules/orders-page/orders/ui/rent-calendar';
import { RentStatus } from '@/components/modules/orders-page/orders/ui/rent-status';
import { useAppDispatch } from '@/store/hooks';
import { showModal } from '@/store/reducers/modal/slice';
import { EOrderStatus, TOrder } from '@/types/orders';

import { setCoordinates } from '@/entities/office';

import { OFFICE_URL } from '@/shared/constants/paths';
import { IMAGE_ALT_CUT_LENGTH, DEFAULT_IMAGE_SRC } from '@/shared/constants/settings';
import { useTranslate } from '@/shared/hooks/use-translate';
import { HideContentByBreakPoint } from '@/shared/ui/hide-by-breakpoint';
import { numbers } from '@/shared/utils/numbers';
import { cutString } from '@/shared/utils/string';

import * as Styled from './styled';

type TProps = {
    photo?: { id: number; image: string; isMain: boolean } | null;
    returned: string | Date;
    order: TOrder;
    pathToProduct: string;
    config: TProductItemConfig;
};

export const Body: FC<TProps> = ({ photo, returned, order, pathToProduct, config }) => {
    const dispatch = useAppDispatch();

    const router = useRouter();

    const { t } = useTranslate();

    const start = config.status === EOrderStatus.Active ? order.create_date : order.terms.issue_date;

    const handleOpenOffice = () => {
        dispatch(setCoordinates(order.rentProduct.store.coordinatesSalePoint));

        router.push(OFFICE_URL);
    };

    const goToProduct = () => {
        router.push(pathToProduct);
    };

    const handleOpenModalDetailsPayment = () => {
        if (order?.repayments?.length > 0) {
            return dispatch(
                showModal({
                    content: <DetailsPayment repayments={order.repayments} prolongations={order.prolongations} />,
                    size: 'Medium',
                    title: t('Детали оплаты'),
                }),
            );
        }

        return dispatch(
            showModal({
                title: t('Оплат еще не было'),
                content: (
                    <MessageSmileModal
                        textBtn={`${t('Понятно')}  🤗`}
                        content={t(
                            'По вашей заявке не было проведено ни одной операции. После первой оплаты вы сможете посмотреть здесь все детали проведенных платежей',
                        )}
                    />
                ),
            }),
        );
    };

    const handleOpenModalCalendarPayment = () => {
        return dispatch(
            showModal({
                content: <RentCalendar number={order.number} />,
                size: 'Medium',
                title: t('График платежей'),
            }),
        );
    };

    return (
        <Styled.Wrapper>
            <HideContentByBreakPoint showIf="md">
                <Styled.ImgContainer className="imgBlock" onClick={goToProduct}>
                    <Image
                        src={photo?.image ?? DEFAULT_IMAGE_SRC}
                        alt={cutString(order.rentProduct.name, IMAGE_ALT_CUT_LENGTH)}
                        fill
                    />
                </Styled.ImgContainer>
            </HideContentByBreakPoint>
            <div className="dataInfo">
                <GeneralInfo order={order} handleOpenOffice={handleOpenOffice}>
                    {config.status !== EOrderStatus.Approved && config.status !== EOrderStatus.Active && (
                        <HideContentByBreakPoint showIf="md">
                            <ButtonDetail
                                config={config}
                                onModalDetailsPayment={handleOpenModalDetailsPayment}
                                onModalCalendarPayment={handleOpenModalCalendarPayment}
                            />
                        </HideContentByBreakPoint>
                    )}
                    {config.status === EOrderStatus.Approved && config.termUnit === 2 && !config.isBuyout && (
                        <Styled.MonthPriceWrapper>
                            <Styled.Line>
                                {t('Ежемесячный платеж по аренде составит')}{' '}
                                {numbers.formatNumberWithSpaces(order?.rentLeaseInfo?.cost ?? 0)} ₸
                            </Styled.Line>
                            <Styled.Line small>{t('Первый платеж наступит через 30 дней')}</Styled.Line>
                        </Styled.MonthPriceWrapper>
                    )}
                </GeneralInfo>
                <RentStatus
                    config={config}
                    order={order}
                    dateRent={{ start, return: returned }}
                    onModalDetailsPayment={handleOpenModalDetailsPayment}
                    onModalCalendarPayment={handleOpenModalCalendarPayment}
                />
            </div>
        </Styled.Wrapper>
    );
};
